import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UIHashComparisonContainer } from 'components/DynamicSegment/UIHashContainers';
import { cloneDeep } from 'lodash';

export interface DynamicSegmentSliceState {
    filtersData: Record<
        string,
        {
            filters: Record<string, UIHashComparisonContainer[]>;
            id: string;
            name: string;
            seqId: number;
            isMain?: boolean;
        }
    >;
    activeTabIndex: string;
}

export const initialState: DynamicSegmentSliceState = {
    filtersData: {},
    activeTabIndex: '0',
};

export const dynamicSegmentSlice = createSlice({
    name: 'dynamicSegment',
    initialState,
    reducers: (create) => ({
        setDynamicSegmentMainSegmentInitialData: create.reducer((state, { payload }: PayloadAction<AnyObject>) => {
            state.filtersData = { ...payload };
        }),
        setDynamicSegmentDefaultFilterList: create.reducer((state, { payload }: PayloadAction<AnyObject>) => {
            state.filtersData = { ...payload };
        }),
        addNewDynamicSegmentFilter: create.reducer(
            (
                state,
                {
                    payload,
                }: PayloadAction<{ data: UIHashComparisonContainer; tab: { id: string; name: string; seqId: number } }>,
            ) => {
                const { data, tab } = payload;
                state.filtersData[tab.id] = {
                    ...state.filtersData[tab.id],
                    filters: {
                        ...state.filtersData[tab.id]?.filters,
                        [data.parentHashKey]: [
                            ...(state.filtersData[tab.id]?.filters?.[data.parentHashKey] ?? []),
                            data,
                        ],
                    },
                    id: tab.id,
                    name: tab.name,
                    seqId: tab.seqId,
                };
            },
        ),
        addNewDynamicSegmentSubSegment: create.reducer((state, { payload }: PayloadAction<AnyObject>) => {
            state.filtersData = { ...state.filtersData, ...payload };
        }),
        changeDynamicSegmentSubSegmentName: create.reducer(
            (state, { payload }: PayloadAction<{ value: string; id: string }>) => {
                const { value, id } = payload;
                const tmpState = cloneDeep(state.filtersData);
                tmpState[id].name = value;
                state.filtersData = { ...tmpState };
            },
        ),
        editDynamicSegmentFilter: create.reducer(
            (
                state,
                {
                    payload,
                }: PayloadAction<{
                    data: UIHashComparisonContainer & { id?: number };
                    tab: { id: string; name: string; seqId: number };
                }>,
            ) => {
                const { data, tab } = payload;
                const tmpState = cloneDeep(state.filtersData);
                const editedState = tmpState[tab.id].filters[data.parentHashKey];
                const index = data.id;
                delete data.id;
                editedState[index ?? 0] = data;
                tmpState[tab.id].filters[data.parentHashKey] = [...editedState];
                state.filtersData = { ...tmpState };
            },
        ),
        deleteDynamicSegmentFilter: create.reducer(
            (
                state,
                { payload }: PayloadAction<{ tabId: string; filterOrderIds: number[]; filterListHashKey: string }>,
            ) => {
                const { tabId, filterOrderIds, filterListHashKey } = payload;
                const tmpState = cloneDeep(state.filtersData);

                tmpState[tabId].filters[filterListHashKey] = tmpState[tabId].filters[filterListHashKey].filter(
                    (_, i) => {
                        return !filterOrderIds.includes(i);
                    },
                );
                if (tmpState[tabId].filters[filterListHashKey].length === 0) {
                    delete tmpState[tabId].filters[filterListHashKey];
                    state.filtersData = { ...tmpState };
                }
                state.filtersData = { ...tmpState };
            },
        ),
        deleteDynamicSegmentFiltersItem: create.reducer(
            (state, { payload }: PayloadAction<{ parentHashKey: string; index: number; id: string }>) => {
                const { parentHashKey, index, id } = payload;
                const tmpState = cloneDeep(state.filtersData);
                tmpState[id].filters[parentHashKey] = tmpState[id].filters[parentHashKey].filter((_, i) => {
                    return i !== index;
                });
                if (tmpState[id].filters[parentHashKey].length < 1) {
                    delete tmpState[id].filters[parentHashKey];
                    state.filtersData = { ...tmpState };
                    return;
                }
                state.filtersData = { ...tmpState };
            },
        ),
        deleteDynamicSegmentFilterSchemaList: create.reducer(
            (state, { payload }: PayloadAction<{ parentHashKeys: string[]; tabId: string }>) => {
                const { parentHashKeys, tabId } = payload;
                const tmpState = cloneDeep(state.filtersData);
                parentHashKeys.forEach((key) => {
                    delete tmpState[tabId].filters[key];
                });
                state.filtersData = { ...tmpState };
            },
        ),
        deleteDynamicSegmentSubSegment: create.reducer((state, { payload }: PayloadAction<string>) => {
            const tmpState = cloneDeep(state.filtersData);
            delete tmpState[payload];
            state.filtersData = { ...tmpState };
        }),
        clearDynamicSegmentFilterList: create.reducer((state) => {
            state.filtersData = {};
        }),
        changeDynamicSegmentActiveTabIndex: create.reducer((state, { payload }: PayloadAction<string | number>) => {
            state.activeTabIndex = `${payload}`;
        }),
    }),
    selectors: {
        selectDynamicSegmentFiltersData: (dynamicSegment) => dynamicSegment.filtersData,
        selectDynamicSegmentActiveTabIndex: (dynamicSegment) => dynamicSegment.activeTabIndex,
    },
});

export const {
    setDynamicSegmentMainSegmentInitialData,
    addNewDynamicSegmentFilter,
    addNewDynamicSegmentSubSegment,
    changeDynamicSegmentSubSegmentName,
    deleteDynamicSegmentSubSegment,
    clearDynamicSegmentFilterList,
    setDynamicSegmentDefaultFilterList,
    editDynamicSegmentFilter,
    deleteDynamicSegmentFilter,
    deleteDynamicSegmentFiltersItem,
    deleteDynamicSegmentFilterSchemaList,
    changeDynamicSegmentActiveTabIndex,
} = dynamicSegmentSlice.actions;
export const { selectDynamicSegmentFiltersData, selectDynamicSegmentActiveTabIndex } = dynamicSegmentSlice.selectors;
